import DocumentCollectionToggle from "./DocumentCollectionSettings/DocumentCollectionToggle";
import DocumentAdd from "./DocumentCollectionSettings/DocumentAdd";
import Documents from "./DocumentCollectionSettings/Documents";

import { useMessagingSettings } from "./useMessagingSettings";

import {
  deleteReferenceDocument,
  postUploadReferenceDocument,
  updateReferenceDocumentPrompt,
} from "../service";

import { Heading } from "V2.0/common/heading";
import DocumentCollectionDelayWarning from "./DocumentCollectionSettings/DocumentCollectionDelayWarning";

export default function DocumentCollectionSettings(props) {
  const { workspaceId, assistantId, syncAssistant, messagingSettings, locked } =
    props;

  const { updateMessagingSettings } = useMessagingSettings({
    syncAssistant,
    workspaceId,
    assistantId,
  });

  const documentCollectionSettings =
    messagingSettings.documentCollectionSettings;

  const documentCollection = documentCollectionSettings.documentCollection;
  const requiredDocuments = documentCollectionSettings.requiredDocuments;

  async function handleAddDocument(payload, lifecycleCbs) {
    lifecycleCbs.init();

    try {
      const response = await postUploadReferenceDocument({
        workspaceId,
        assistantId,
        payload,
      });

      const { status, data } = response;

      if (status === 200) {
        lifecycleCbs.success();

        syncAssistant((prevAssistant) => {
          const updatedDocumentCollectionSettings = {
            ...documentCollectionSettings,
            requiredDocuments: [...requiredDocuments, data],
          };

          const updatedMessagingSettings = {
            ...messagingSettings,
            documentCollectionSettings: updatedDocumentCollectionSettings,
          };

          return {
            ...prevAssistant,
            messagingSettings: updatedMessagingSettings,
          };
        });
      }
    } catch (e) {
      lifecycleCbs.error(e);
    }
  }

  async function handleDeleteDocument(payload, lifecycleCbs) {
    lifecycleCbs.init();

    try {
      const response = await deleteReferenceDocument({
        workspaceId,
        assistantId,
        payload,
      });
      const { data, status } = response;

      if (status === 200) {
        lifecycleCbs.success();

        syncAssistant((prevAssistant) => {
          return {
            ...prevAssistant,
            messagingSettings: data,
          };
        });
      }
    } catch (e) {
      lifecycleCbs.error(e);
    }
  }

  async function handleUpdateDocumentPrompt(payload, lifecycleCbs) {
    lifecycleCbs.init(payload);

    try {
      const response = await updateReferenceDocumentPrompt({
        workspaceId,
        assistantId,
        payload,
      });

      const { data, status } = response;

      if (status === 200) {
        lifecycleCbs.success(payload);

        syncAssistant((prevAssistant) => {
          const messagingSettings = prevAssistant.messagingSettings;

          return {
            ...prevAssistant,
            messagingSettings: {
              ...messagingSettings,
              documentCollectionSettings: data.result,
            },
          };
        });
      }
    } catch (e) {
      lifecycleCbs.error(e, payload);
    }
  }

  function handleToggleDocumentCollection(isDocumentCollectionEnabled) {
    let payload: any = {
      documentCollectionSettings: {
        documentCollection: isDocumentCollectionEnabled,
        requiredDocuments,
      },
    };

    if (isDocumentCollectionEnabled) {
      payload = {
        messageDelay: 20,
        documentCollectionSettings: {
          documentCollection: isDocumentCollectionEnabled,
          requiredDocuments,
        },
      };
    }

    updateMessagingSettings(payload);
  }

  return (
    <div className="flex flex-col gap-y-4 w-3/4 m-4 min-w-[600px] border rounded-lg">
      <div className="flex justify-between items-center bg-gray-50 px-6 py-4 rounded-t-lg">
        <div className="flex flex-col gap-y-1">
          <div className="flex items-center gap-x-2">
            <p className="text-md font-semibold text-gray-900">
              Document Collection
            </p>
            {!locked && (
              <DocumentCollectionToggle
                isEnabled={documentCollection}
                onToggleDocumentCollection={handleToggleDocumentCollection}
              />
            )}
          </div>
          <DocumentCollectionDelayWarning isEnabled={documentCollection} />
        </div>
        {!locked && (
          <div className="flex-none">
            <DocumentAdd onDocumentAdd={handleAddDocument} />
          </div>
        )}
      </div>
      <div>
        <Documents
          documents={requiredDocuments}
          onDocumentDelete={handleDeleteDocument}
          onDocumentPromptUpdate={handleUpdateDocumentPrompt}
          locked={locked}
        />
      </div>
    </div>
  );
}
